import { useRef, useState } from 'react';
import { container } from '@infrastructure/di/container';
import { runScript } from '@presentation/services/scriptSandbox';
import {
  buildDocumentPayload,
  buildHeaderMap,
  buildVariableMap,
  flattenResponseHeaders,
  mergeVariableChanges,
} from '@presentation/utils/apiRequestPayload';
import { useConsoleStore } from '@presentation/stores/consoleStore';
import type { ApiRequest } from '@core/entities/ApiRequest';

export type RunnerRequestStatus = 'pending' | 'running' | 'passed' | 'failed' | 'error' | 'skipped';

export interface RunnerResult {
  key: string;
  requestId: string;
  requestName: string;
  method: string;
  url: string;
  iteration: number;
  status: RunnerRequestStatus;
  statusCode: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  testsPassed: number;
  testsTotal: number;
}

export interface RunnerSummary {
  totalRuns: number;
  passed: number;
  failed: number;
  errored: number;
  totalDurationMs: number;
  assertionsPassed: number;
  assertionsTotal: number;
}

interface RunOptions {
  iterations: number;
  delayMs: number;
}

const summarize = (results: RunnerResult[]): RunnerSummary =>
  results.reduce<RunnerSummary>(
    (summary, result) => ({
      totalRuns: summary.totalRuns + 1,
      passed: summary.passed + (result.status === 'passed' ? 1 : 0),
      failed: summary.failed + (result.status === 'failed' ? 1 : 0),
      errored: summary.errored + (result.status === 'error' ? 1 : 0),
      totalDurationMs: summary.totalDurationMs + (result.durationMs ?? 0),
      assertionsPassed: summary.assertionsPassed + result.testsPassed,
      assertionsTotal: summary.assertionsTotal + result.testsTotal,
    }),
    {
      totalRuns: 0,
      passed: 0,
      failed: 0,
      errored: 0,
      totalDurationMs: 0,
      assertionsPassed: 0,
      assertionsTotal: 0,
    },
  );

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs every request in a collection sequentially, N iterations, replaying
 * the exact same pre-request-script -> send -> test-script flow the single
 * "Send" button uses (via the shared apiRequestPayload helpers) so behavior
 * is identical to running each request by hand.
 *
 * Known, deliberate simplification: pre-request/test script variable
 * changes (`pm.environment.set(...)`) are recorded in each result but are
 * NOT fed forward into later requests' variable resolution within the same
 * run — that resolution happens server-side from the environment's saved
 * values, and replicating Postman's live variable-mutation-during-a-run
 * semantics would need either a full client-side variable resolver or a
 * write-back to the database after every script step. Each request still
 * runs correctly against its own saved configuration and the environment
 * as currently saved.
 */
export const useRunCollection = () => {
  const [results, setResults] = useState<RunnerResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const stopRequested = useRef(false);
  const addLog = useConsoleStore((state) => state.add);

  const runOne = async (request: ApiRequest, iteration: number): Promise<RunnerResult> => {
    const key = `${request.id}-${iteration}-${crypto.randomUUID()}`;
    const requestContext = {
      method: request.method,
      url: request.url,
      headers: buildHeaderMap(request.headers),
    };
    let variableChanges: Record<string, string> = {};

    try {
      if (request.preRequestScript?.trim()) {
        const preResult = runScript({
          script: request.preRequestScript,
          request: requestContext,
          variables: buildVariableMap(request.variables),
        });
        preResult.logs.forEach((log) =>
          addLog({
            level: log.level,
            message: log.message,
            source: `Runner · ${request.name} (pre-request)`,
          }),
        );
        if (preResult.error) {
          throw new Error(`Pre-request script error: ${preResult.error}`);
        }
        variableChanges = preResult.variableChanges;
      }

      const overrides =
        Object.keys(variableChanges).length > 0
          ? mergeVariableChanges(buildDocumentPayload(request), variableChanges)
          : undefined;
      const entry = await container.apiRequests.send(request.id, overrides);

      let testsPassed = 0;
      let testsTotal = 0;
      let scriptError: string | null = null;

      if (request.testScript?.trim()) {
        const responseContext = entry.response
          ? {
              code: entry.statusCode ?? 0,
              status: entry.response.statusText ?? '',
              responseTimeMs: entry.durationMs ?? 0,
              headers: flattenResponseHeaders(entry.response.headers),
              body: entry.response.body ?? '',
            }
          : undefined;

        const testResult = runScript({
          script: request.testScript,
          request: requestContext,
          variables: { ...buildVariableMap(request.variables), ...variableChanges },
          response: responseContext,
        });
        testResult.logs.forEach((log) =>
          addLog({
            level: log.level,
            message: log.message,
            source: `Runner · ${request.name} (test)`,
          }),
        );
        testsTotal = testResult.tests.length;
        testsPassed = testResult.tests.filter((test) => test.passed).length;
        scriptError = testResult.error;
      }

      const status: RunnerRequestStatus = entry.errorMessage
        ? 'error'
        : scriptError || (testsTotal > 0 && testsPassed < testsTotal)
          ? 'failed'
          : 'passed';

      return {
        key,
        requestId: request.id,
        requestName: request.name,
        method: request.method,
        url: request.url,
        iteration,
        status,
        statusCode: entry.statusCode,
        durationMs: entry.durationMs,
        errorMessage: entry.errorMessage ?? scriptError,
        testsPassed,
        testsTotal,
      };
    } catch (error) {
      return {
        key,
        requestId: request.id,
        requestName: request.name,
        method: request.method,
        url: request.url,
        iteration,
        status: 'error',
        statusCode: null,
        durationMs: null,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        testsPassed: 0,
        testsTotal: 0,
      };
    }
  };

  const run = async (requests: ApiRequest[], options: RunOptions) => {
    setResults([]);
    setIsRunning(true);
    stopRequested.current = false;

    const collected: RunnerResult[] = [];

    try {
      for (let iteration = 1; iteration <= options.iterations; iteration++) {
        for (const request of requests) {
          if (stopRequested.current) break;

          const running: RunnerResult = {
            key: `${request.id}-${iteration}-pending`,
            requestId: request.id,
            requestName: request.name,
            method: request.method,
            url: request.url,
            iteration,
            status: 'running',
            statusCode: null,
            durationMs: null,
            errorMessage: null,
            testsPassed: 0,
            testsTotal: 0,
          };
          collected.push(running);
          setResults([...collected]);

          const result = await runOne(request, iteration);
          collected[collected.length - 1] = result;
          setResults([...collected]);

          if (options.delayMs > 0 && !stopRequested.current) {
            await sleep(options.delayMs);
          }
        }
        if (stopRequested.current) break;
      }
    } finally {
      setIsRunning(false);
    }
  };

  const stop = () => {
    stopRequested.current = true;
  };

  return { run, stop, isRunning, results, summary: summarize(results) };
};
